import { useCallback, useEffect, useState } from 'react';
import {
  RefreshCw,
  Target,
  Banknote,
  TrendingUp,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { AppShell } from '@/components/layout/AppShell';
import { Header } from '@/components/layout/Header';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton, SkeletonCard } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';
import { cn } from '@/lib/cn';

/* ==========================================================================
   Revenue — Obiettivi di fatturato.

   Widget card (target, effettivo, %), barra progresso,
   grafico storico, refresh manuale (dati da n8n/dbgym).
   ========================================================================== */

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

interface RevenueData {
  period: string;
  targetAmount: string;
  actualAmount: string;
  percentage: number;
  lastSyncedAt: string | null;
}

/* --------------------------------------------------------------------------
   Helpers
   -------------------------------------------------------------------------- */

const currencyFmt = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const MONTH_LABELS: Record<string, string> = {
  '01': 'Gen',
  '02': 'Feb',
  '03': 'Mar',
  '04': 'Apr',
  '05': 'Mag',
  '06': 'Giu',
  '07': 'Lug',
  '08': 'Ago',
  '09': 'Set',
  '10': 'Ott',
  '11': 'Nov',
  '12': 'Dic',
};

function formatMonthLabel(period: string): string {
  const month = period.split('-')[1];
  return MONTH_LABELS[month] ?? month;
}

function formatTimeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Mai sincronizzato';
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Adesso';
  if (minutes < 60) return `${minutes} min fa`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h fa`;
  const days = Math.floor(hours / 24);
  return `${days}g fa`;
}

function percentageColor(pct: number): {
  text: string;
  bg: string;
  bar: string;
} {
  if (pct >= 80) return { text: 'text-status-positive', bg: 'bg-status-positive-light', bar: 'bg-status-positive' };
  if (pct >= 50) return { text: 'text-amber-600', bg: 'bg-amber-50', bar: 'bg-amber-400' };
  return { text: 'text-status-negative', bg: 'bg-status-negative-light', bar: 'bg-status-negative' };
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function Revenue() {
  const toast = useToast();

  const [current, setCurrent] = useState<RevenueData | null>(null);
  const [history, setHistory] = useState<RevenueData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ------------------------------------------------------------------
  // Load data
  // ------------------------------------------------------------------

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [currentData, historyData] = await Promise.all([
        api.get<RevenueData>('/revenue/current'),
        api.get<RevenueData[]>('/revenue/history?months=6'),
      ]);
      setCurrent(currentData ?? null);
      setHistory(historyData ?? []);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Errore di connessione');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ------------------------------------------------------------------
  // Refresh handler
  // ------------------------------------------------------------------

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await api.post('/revenue/refresh');
      toast.success('Richiesta di aggiornamento inviata');
      // Reload after a delay to allow n8n to process
      setTimeout(() => {
        void loadData();
      }, 5000);
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error(err.message);
      } else {
        toast.error('Errore durante la richiesta di aggiornamento');
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [loadData, toast]);

  // ------------------------------------------------------------------
  // Derived values
  // ------------------------------------------------------------------

  const target = current ? parseFloat(current.targetAmount) : 0;
  const actual = current ? parseFloat(current.actualAmount) : 0;
  const pct = current?.percentage ?? 0;
  const colors = percentageColor(pct);

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  if (isLoading) {
    return (
      <AppShell header={<Header title="Obiettivi Fatturato" showBack />}>
        <div className="space-y-6">
          <Skeleton height={16} className="w-2/5" />
          <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3].map((i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
          <Skeleton height={48} className="w-full rounded-[var(--radius-lg)]" />
          <Skeleton height={200} className="w-full rounded-[var(--radius-lg)]" />
        </div>
      </AppShell>
    );
  }

  if (error) {
    return (
      <AppShell header={<Header title="Obiettivi Fatturato" showBack />}>
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <p className="text-sm text-text-muted mb-4">{error}</p>
          <Button variant="secondary" size="sm" onClick={() => void loadData()}>
            Riprova
          </Button>
        </div>
      </AppShell>
    );
  }

  const hasData = target > 0 || actual > 0;

  return (
    <AppShell
      header={
        <Header
          title="Obiettivi Fatturato"
          showBack
          actions={
            <button
              onClick={() => void handleRefresh()}
              disabled={isRefreshing}
              className={cn(
                'flex items-center justify-center',
                'h-[var(--touch-min)] w-[var(--touch-min)]',
                'rounded-[var(--radius-md)]',
                'text-text-primary',
                'transition-colors duration-[var(--duration-fast)]',
                'hover:bg-surface-sunken active:bg-border-default',
                'cursor-pointer',
                'disabled:opacity-45 disabled:pointer-events-none',
              )}
              aria-label="Aggiorna dati"
            >
              <RefreshCw
                className={cn('h-5 w-5', isRefreshing && 'animate-spin')}
                strokeWidth={2}
              />
            </button>
          }
        />
      }
    >
      {/* Ultimo aggiornamento */}
      <p className="text-xs text-text-muted mb-6">
        Ultimo aggiornamento: {formatTimeAgo(current?.lastSyncedAt ?? null)}
      </p>

      {!hasData ? (
        /* ---- Empty state ---- */
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Target className="h-12 w-12 text-text-muted/40 mb-4" strokeWidth={1} />
          <p className="text-sm text-text-muted">
            Nessun dato disponibile.
          </p>
          <p className="text-xs text-text-muted mt-1">
            Attendi la prossima sincronizzazione o premi il pulsante di aggiornamento.
          </p>
        </div>
      ) : (
        <>
          {/* ---- Widget cards ---- */}
          <div className="mb-8 grid grid-cols-3 gap-3">
            {/* Target mensile */}
            <Card padding="sm" shadow="sm" className="text-center">
              <div
                className={cn(
                  'mx-auto mb-2 flex h-10 w-10 items-center justify-center',
                  'rounded-[var(--radius-md)]',
                  'bg-status-info-light',
                )}
              >
                <Target className="h-5 w-5 text-status-info" strokeWidth={1.5} />
              </div>
              <p className="font-[family-name:var(--font-heading)] text-lg font-bold text-text-primary tabular-nums">
                {currencyFmt.format(target)}
              </p>
              <p className="text-xs text-text-muted mt-0.5 leading-tight">Target mensile</p>
            </Card>

            {/* Cassa F3 (= netto: positivi - resi, tutti i tipi pagamento) */}
            <Card padding="sm" shadow="sm" className="text-center">
              <div
                className={cn(
                  'mx-auto mb-2 flex h-10 w-10 items-center justify-center',
                  'rounded-[var(--radius-md)]',
                  'bg-accent-secondary-light',
                )}
              >
                <Banknote className="h-5 w-5 text-accent-secondary" strokeWidth={1.5} />
              </div>
              <p className="font-[family-name:var(--font-heading)] text-lg font-bold text-text-primary tabular-nums">
                {currencyFmt.format(actual)}
              </p>
              <p className="text-xs text-text-muted mt-0.5 leading-tight">Cassa F3</p>
            </Card>

            {/* Raggiungimento */}
            <Card padding="sm" shadow="sm" className="text-center">
              <div
                className={cn(
                  'mx-auto mb-2 flex h-10 w-10 items-center justify-center',
                  'rounded-[var(--radius-md)]',
                  colors.bg,
                )}
              >
                <TrendingUp className={cn('h-5 w-5', colors.text)} strokeWidth={1.5} />
              </div>
              <p className={cn(
                'font-[family-name:var(--font-heading)] text-lg font-bold tabular-nums',
                colors.text,
              )}>
                {pct.toFixed(1)}%
              </p>
              <p className="text-xs text-text-muted mt-0.5 leading-tight">Raggiungimento</p>
            </Card>
          </div>

          {/* ---- Progress bar ---- */}
          <Card padding="md" shadow="sm" className="mb-8">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-text-muted">Progresso mese corrente</span>
                <span className={cn('font-semibold', colors.text)}>
                  {pct.toFixed(1)}%
                </span>
              </div>
              <div className="h-4 rounded-full bg-surface-sunken overflow-hidden">
                <div
                  className={cn(
                    'h-full rounded-full transition-all duration-700',
                    colors.bar,
                  )}
                  style={{ width: `${Math.min(100, pct)}%` }}
                />
              </div>
              <div className="flex justify-between text-xs text-text-muted">
                <span>{currencyFmt.format(actual)}</span>
                <span>Target: {currencyFmt.format(target)}</span>
              </div>
            </div>
          </Card>

          {/* ---- History chart ---- */}
          {history.length > 0 && <RevenueChart data={history} />}
        </>
      )}

      {/* Bottom spacer */}
      <div className="h-8" />
    </AppShell>
  );
}

/* --------------------------------------------------------------------------
   Revenue Chart — CSS-only bar chart (no external library)
   -------------------------------------------------------------------------- */

function RevenueChart({ data }: { data: RevenueData[] }) {
  // Sort oldest → newest (left to right)
  const sorted = [...data].sort((a, b) => a.period.localeCompare(b.period));

  const maxAmount = Math.max(
    ...sorted.flatMap((d) => [parseFloat(d.targetAmount), parseFloat(d.actualAmount)]),
    1, // avoid division by zero
  );

  return (
    <Card padding="md" shadow="sm">
      <h3 className="font-[family-name:var(--font-heading)] text-base font-bold text-text-primary mb-4">
        Andamento ultimi mesi
      </h3>

      <div className="flex items-end gap-3 h-48">
        {sorted.map((item) => {
          const targetVal = parseFloat(item.targetAmount);
          const actualVal = parseFloat(item.actualAmount);
          const targetHeight = (targetVal / maxAmount) * 100;
          const actualHeight = (actualVal / maxAmount) * 100;
          const monthLabel = formatMonthLabel(item.period);
          const reachedTarget = actualVal >= targetVal;

          return (
            <div key={item.period} className="flex-1 flex flex-col items-center gap-1">
              {/* Percentage label */}
              <span className={cn(
                'text-[10px] font-semibold tabular-nums',
                reachedTarget ? 'text-status-positive' : 'text-text-muted',
              )}>
                {item.percentage.toFixed(0)}%
              </span>

              {/* Bars container */}
              <div className="flex items-end gap-0.5 w-full h-36">
                {/* Target bar */}
                <div
                  className="flex-1 bg-surface-sunken rounded-t-[var(--radius-xs)] transition-all duration-500"
                  style={{ height: `${targetHeight}%` }}
                />
                {/* Actual bar */}
                <div
                  className={cn(
                    'flex-1 rounded-t-[var(--radius-xs)] transition-all duration-500',
                    reachedTarget ? 'bg-status-positive' : 'bg-accent-primary',
                  )}
                  style={{ height: `${actualHeight}%` }}
                />
              </div>

              {/* Month label */}
              <span className="text-xs text-text-muted">{monthLabel}</span>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-text-muted mt-4">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-surface-sunken border border-border-default" />
          Target
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-accent-primary" />
          Cassa F3
        </span>
      </div>
    </Card>
  );
}
